import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { getApiBaseUrl } from '../../config/apiConfig';
import { DataContext } from '../context/DataContext';
import {
  FaBolt,
  FaSave,
  FaPlus,
  FaTrash,
  FaCalendarAlt,
  FaInfoCircle,
} from 'react-icons/fa';
import { FiUsers, FiZap, FiCheckCircle, FiXCircle } from 'react-icons/fi';

const WORKFLOW_TYPES = [
  {
    value: 'comment-reply',
    label: 'Comment Reply',
    desc: 'Auto-reply to comments on a post',
  },
  {
    value: 'comment-dm',
    label: 'Comment → DM',
    desc: 'Send DM when someone comments',
  },
  {
    value: 'dm',
    label: 'Direct Message',
    desc: 'Reply to incoming DMs',
  },
];

const PLAN_TIERS = ['basic', 'pro', 'enterprise'];
const CRITERION_TYPES = [
  { value: 'min_followers', label: 'Minimum Followers' },
  { value: 'keyword_in_bio', label: 'Keyword in Bio' },
  { value: 'follow_accounts', label: 'Must Follow Account(s)' },
];

const reactSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 42,
    borderColor: '#D1D5DB',
    boxShadow: 'none',
    ':hover': { borderColor: '#6366F1' },
  }),
};

export default function EventAutomationConfig({ eventId: propEventId }) {
  const params = useParams();
  const { allEventsSummary, fetchAllEventNames } = useContext(DataContext);

  const [eventId, setEventIdState] = useState(() => {
    return (
      propEventId ||
      params.eventId ||
      localStorage.getItem('lastAutomationEventId') ||
      ''
    );
  });

  const setEventId = (id) => {
    setEventIdState(String(id || ''));
    if (id) {
      localStorage.setItem('lastAutomationEventId', String(id));
    } else {
      localStorage.removeItem('lastAutomationEventId');
    }
  };

  const [config, setConfig] = useState({
    event_id: '',
    active: false,
    default_workflow_type: 'comment-reply',
    default_system_prompt: '',
    default_criteria: [],
    allow_user_override: false,
    required_plan: 'basic',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const token = Cookies.get('fbauthToken');
  const API = getApiBaseUrl();

  useEffect(() => {
    if (allEventsSummary.length === 0) {
      fetchAllEventNames();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!eventId) return;
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/v2/automation/config?event_id=${eventId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setConfig({
        ...res.data,
        default_criteria: res.data.default_criteria || [],
      });
    } catch (err) {
      if (err.response?.status === 404) {
        setConfig({
          event_id: eventId,
          active: false,
          default_workflow_type: 'comment-reply',
          default_system_prompt: '',
          default_criteria: [],
          allow_user_override: false,
          required_plan: 'basic',
        });
      } else {
        setMessage({ type: 'error', text: 'Failed to load automation config' });
      }
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await axios.put(
        `${API}/api/v2/automation/config?event_id=${eventId}`,
        { ...config, event_id: String(eventId) },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMessage({ type: 'success', text: 'Automation config saved successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save config' });
    }
    setSaving(false);
  };

  const addCriterion = () => {
    setConfig((prev) => ({
      ...prev,
      default_criteria: [
        ...(prev.default_criteria || []),
        {
          id: 'c' + Date.now(),
          type: 'min_followers',
          params: { minCount: 100 },
          fail_message: 'You need at least 100 followers to participate.',
        },
      ],
    }));
  };

  const removeCriterion = (id) => {
    setConfig((prev) => ({
      ...prev,
      default_criteria: (prev.default_criteria || []).filter((c) => c.id !== id),
    }));
  };

  const updateCriterion = (id, field, value) => {
    setConfig((prev) => ({
      ...prev,
      default_criteria: (prev.default_criteria || []).map((c) =>
        c.id === id ? { ...c, [field]: value } : c,
      ),
    }));
  };

  const updateCriterionParams = (id, paramKey, value) => {
    setConfig((prev) => ({
      ...prev,
      default_criteria: (prev.default_criteria || []).map((c) =>
        c.id === id ? { ...c, params: { ...c.params, [paramKey]: value } } : c,
      ),
    }));
  };

  const eventOptions = allEventsSummary.map((e) => ({
    value: String(e.event_id),
    label: `${e.event_name} (${e.event_id})`,
  }));

  const selectedEventOption =
    eventOptions.find((opt) => String(opt.value) === String(eventId)) || null;

  return (
    <div className="lg:p-1 min-h-screen lg:mr-[2vw] bg-gray-50 pb-24">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold text-indigo-800 flex items-center gap-2">
            <FaBolt />
            <span>Event Automation</span>
          </h2>
          <p className="text-gray-600 mt-1">
            Configure AI-powered auto-reply behaviour for an event's Instagram posts.
          </p>
        </div>

        <div className="flex items-center gap-3 mt-3 md:mt-0">
          {config.active ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 text-green-700 border border-green-200 text-sm font-semibold">
              <FiCheckCircle /> Live
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200 text-sm font-semibold">
              <FiXCircle /> Paused
            </span>
          )}
          <span className="px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-sm font-semibold capitalize">
            {config.required_plan} plan
          </span>
        </div>
      </div>

      {/* Top inline notice */}
      {message && (
        <div
          className={`mb-4 px-4 py-3 rounded-lg text-sm flex items-center gap-2 border ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-red-50 text-red-700 border-red-200'
          }`}
        >
          {message.type === 'success' ? <FiCheckCircle /> : <FiXCircle />}
          <span className="font-medium">{message.text}</span>
        </div>
      )}

      {/* Event picker */}
      {!propEventId && !params.eventId && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
          <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
            <FaCalendarAlt className="inline mr-1.5 text-indigo-500" />
            Select Event to Configure
          </label>
          <Select
            options={eventOptions}
            value={selectedEventOption}
            onChange={(opt) => setEventId(opt ? opt.value : '')}
            placeholder="Search and select an event..."
            className="text-sm"
            styles={reactSelectStyles}
            isSearchable
            isClearable
          />
          {!eventId && (
            <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
              <FaInfoCircle /> Pick an event to view or edit its automation
              configuration.
            </p>
          )}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center min-h-[300px] bg-white rounded-xl border border-gray-200">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
        </div>
      )}

      {!loading && eventId && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: switches + plan */}
          <div className="lg:col-span-1 space-y-6">
            {/* Active toggle card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-start gap-3 mb-3">
                <div className="bg-indigo-50 text-indigo-600 p-2 rounded-lg">
                  <FiZap />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800">Automation Active</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Master switch for AI replies on this event.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() =>
                  setConfig((prev) => ({ ...prev, active: !prev.active }))
                }
                role="switch"
                aria-checked={config.active}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                  config.active ? 'bg-indigo-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
                    config.active ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Required plan card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-800 mb-1">
                Required Subscription Plan
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                Minimum tier subscribers need to enable automation for this event.
              </p>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white capitalize"
                value={config.required_plan}
                onChange={(e) =>
                  setConfig((prev) => ({ ...prev, required_plan: e.target.value }))
                }
              >
                {PLAN_TIERS.map((t) => (
                  <option key={t} value={t} className="capitalize">
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* User override card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-start gap-3 mb-3">
                <div className="bg-purple-50 text-purple-600 p-2 rounded-lg">
                  <FiUsers />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800">
                    Allow User Prompt Override
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Let subscribers customise the AI prompt for their own posts.
                  </p>
                </div>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.allow_user_override}
                  onChange={(e) =>
                    setConfig((prev) => ({
                      ...prev,
                      allow_user_override: e.target.checked,
                    }))
                  }
                  className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">
                  Allow override per-post
                </span>
              </label>
            </div>
          </div>

          {/* Right column: workflow, prompt, criteria */}
          <div className="lg:col-span-2 space-y-6">
            {/* Workflow type */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-800 mb-1">
                Default Workflow Type
              </h3>
              <p className="text-xs text-gray-500 mb-4">
                What kind of interaction triggers an AI response by default.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {WORKFLOW_TYPES.map((wt) => {
                  const selected = config.default_workflow_type === wt.value;
                  return (
                    <button
                      key={wt.value}
                      type="button"
                      onClick={() =>
                        setConfig((prev) => ({
                          ...prev,
                          default_workflow_type: wt.value,
                        }))
                      }
                      className={`text-left rounded-lg border p-3 transition-all ${
                        selected
                          ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-200'
                          : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`h-3.5 w-3.5 rounded-full border ${
                            selected
                              ? 'border-indigo-500 bg-indigo-500'
                              : 'border-gray-300'
                          }`}
                        />
                        <span
                          className={`text-sm font-semibold ${
                            selected ? 'text-indigo-800' : 'text-gray-800'
                          }`}
                        >
                          {wt.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 pl-5">{wt.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* System prompt */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-gray-800">
                  Default AI System Prompt
                </h3>
                <span className="text-xs text-gray-400">
                  {(config.default_system_prompt || '').length} chars
                </span>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                Used by GPT to generate replies — be specific about tone and goals.
              </p>
              <textarea
                rows={8}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono leading-relaxed"
                placeholder="You are the official AI assistant for this event. Reply warmly, guide users to participate, and keep responses concise..."
                value={config.default_system_prompt}
                onChange={(e) =>
                  setConfig((prev) => ({
                    ...prev,
                    default_system_prompt: e.target.value,
                  }))
                }
              />
            </div>

            {/* Criteria */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-start justify-between mb-3 gap-4">
                <div>
                  <h3 className="font-semibold text-gray-800">
                    Automation Criteria
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Users must meet all criteria to receive automated responses.
                  </p>
                </div>
                <button
                  onClick={addCriterion}
                  type="button"
                  className="flex-shrink-0 inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 px-3 py-1.5 rounded-lg text-xs font-semibold border border-indigo-200"
                >
                  <FaPlus /> Add Criterion
                </button>
              </div>

              {(!config.default_criteria || config.default_criteria.length === 0) && (
                <div className="text-center py-8 text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg bg-gray-50">
                  No criteria set — all users will receive automation responses.
                </div>
              )}

              <div className="space-y-3">
                {(config.default_criteria || []).map((criterion) => (
                  <div
                    key={criterion.id}
                    className="border border-gray-200 rounded-lg p-4 bg-gray-50/50"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 space-y-3">
                        <select
                          className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                          value={criterion.type}
                          onChange={(e) =>
                            updateCriterion(criterion.id, 'type', e.target.value)
                          }
                        >
                          {CRITERION_TYPES.map((ct) => (
                            <option key={ct.value} value={ct.value}>
                              {ct.label}
                            </option>
                          ))}
                        </select>

                        {criterion.type === 'min_followers' && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">
                              Minimum:
                            </span>
                            <input
                              type="number"
                              className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                              value={criterion.params?.minCount || 0}
                              onChange={(e) =>
                                updateCriterionParams(
                                  criterion.id,
                                  'minCount',
                                  parseInt(e.target.value, 10) || 0,
                                )
                              }
                            />
                            <span className="text-sm text-gray-400">followers</span>
                          </div>
                        )}
                        {criterion.type === 'keyword_in_bio' && (
                          <div>
                            <span className="text-xs text-gray-500">
                              Keywords (comma-separated)
                            </span>
                            <input
                              type="text"
                              className="mt-1 border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                              placeholder="fitness, yoga, wellness"
                              value={(criterion.params?.keywords || []).join(', ')}
                              onChange={(e) =>
                                updateCriterionParams(
                                  criterion.id,
                                  'keywords',
                                  e.target.value
                                    .split(',')
                                    .map((k) => k.trim())
                                    .filter(Boolean),
                                )
                              }
                            />
                          </div>
                        )}
                        {criterion.type === 'follow_accounts' && (
                          <div>
                            <span className="text-xs text-gray-500">
                              Account IDs (comma-separated)
                            </span>
                            <input
                              type="text"
                              className="mt-1 border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                              placeholder="17841234567, 17849876543"
                              value={(criterion.params?.accountIds || []).join(
                                ', ',
                              )}
                              onChange={(e) =>
                                updateCriterionParams(
                                  criterion.id,
                                  'accountIds',
                                  e.target.value
                                    .split(',')
                                    .map((k) => k.trim())
                                    .filter(Boolean),
                                )
                              }
                            />
                          </div>
                        )}

                        <div>
                          <span className="text-xs text-gray-500">
                            Message shown if criterion not met
                          </span>
                          <input
                            type="text"
                            className="mt-1 border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                            placeholder="Message sent to user if they don't meet this criterion..."
                            value={criterion.fail_message || ''}
                            onChange={(e) =>
                              updateCriterion(
                                criterion.id,
                                'fail_message',
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => removeCriterion(criterion.id)}
                        type="button"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                        aria-label="Remove criterion"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && !eventId && (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 py-16 text-center">
          <FaBolt className="mx-auto text-4xl text-gray-300 mb-3" />
          <h3 className="text-gray-600 font-medium">No event selected</h3>
          <p className="text-gray-400 text-sm">
            Choose an event above to view or edit its automation config.
          </p>
        </div>
      )}

      {/* Sticky save bar */}
      {eventId && (
        <div className="fixed bottom-0 left-0 right-0 lg:left-[260px] bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.04)] z-30">
          <div className="lg:mr-[2vw] px-4 py-3 flex items-center justify-between">
            <span className="text-xs text-gray-500 hidden sm:block">
              Editing event ID{' '}
              <span className="font-mono text-gray-700">{eventId}</span>
            </span>
            <button
              onClick={handleSave}
              disabled={saving || !eventId}
              type="button"
              className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
            >
              <FaSave />{' '}
              {saving ? 'Saving…' : 'Save Configuration'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
